import { Box, ButtonBase, styled, Typography } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import {
  EPOCUS_AMBER,
  EPOCUS_AMBER_BG,
  EPOCUS_AMBER_BORDER,
  EPOCUS_BORDER,
  EPOCUS_RED,
  EPOCUS_RED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { linkToEpocusExam } from "../../links";
import {
  type IncompleteStudy,
  useIncompleteStudies,
} from "./use-incomplete-studies";

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 10,
});

const SectionTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const CountBadge = styled(Box)({
  backgroundColor: EPOCUS_AMBER,
  color: EPOCUS_WHITE,
  borderRadius: 10,
  fontSize: rem(12),
  fontWeight: 700,
  minWidth: 20,
  height: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 6px",
});

const Row = styled(ButtonBase)({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  textAlign: "left",
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: "12px 14px",
});

const RowInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 0,
});

const RowTitleLine = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 6,
});

const Worksheet = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const Detail = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const Reasons = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_AMBER,
  fontWeight: 600,
});

const OverdueTag = styled(Box)({
  backgroundColor: EPOCUS_RED_BG,
  color: EPOCUS_RED,
  fontSize: rem(10),
  fontWeight: 700,
  letterSpacing: "0.06em",
  padding: "1px 6px",
  borderRadius: 4,
});

const Callout = styled(Box)({
  backgroundColor: EPOCUS_AMBER_BG,
  border: `1px solid ${EPOCUS_AMBER_BORDER}`,
  borderRadius: 12,
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const IncompleteStudyRow: FC<{ study: IncompleteStudy }> = ({ study }) => {
  const navigate = useNavigate();
  return (
    <Row
      onClick={() =>
        navigate(linkToEpocusExam(study.encounterId, study.examId))
      }
    >
      <RowInfo>
        <RowTitleLine>
          <Worksheet>{study.worksheet}</Worksheet>
          {study.overdue && <OverdueTag>OVERDUE</OverdueTag>}
        </RowTitleLine>
        <Detail>{formatDate(study.date)}</Detail>
        <Reasons>{study.reasons.join(" · ")}</Reasons>
      </RowInfo>
      <ChevronRightIcon style={{ color: EPOCUS_TEXT_SECONDARY }} />
    </Row>
  );
};

export const IncompleteStudiesSection: FC = () => {
  const studies = useIncompleteStudies();
  if (studies.length === 0) return null;

  return (
    <Box component="section" aria-labelledby="incomplete-studies-title">
      <SectionHeader>
        <SectionTitle id="incomplete-studies-title">Incomplete</SectionTitle>
        <CountBadge>{studies.length}</CountBadge>
      </SectionHeader>
      <Callout>
        {studies.map((study) => (
          <IncompleteStudyRow key={study.examId} study={study} />
        ))}
      </Callout>
    </Box>
  );
};
